import React from "react";
import { confirmMessage } from "../../common/components/confirm";
import FileInput from "./fileInput";

import AttachmentsActions from "../attachmentsActions";
import { attach } from "../../common/componentExtensions";
import AttachmentsStore from "../attachmentsStore";
import AttachmentPreview from "./attachmentPreview";
import { AttachmentInfo, ToAttachmentsInfo } from "../attachment";
import { AttachmentDto } from "../../generated";

interface Props {
    resourceName: string;
    resourceId: string | null;
    remoteFiles: AttachmentDto[];
    acceptedExtensions?: string[];
    onFilesAdded?: (pendingFiles: AttachmentInfo[]) => void;
    onUploadStart?: (pendingFiles: AttachmentInfo[]) => void;
    onUploadEnd?: (uploadedFiles: AttachmentInfo[]) => void;
}

interface State {
    status: string | null;
    pendingFiles: File[] | null;
}

function usePrevious<T>(value: T) {
    const ref = React.useRef<T>();
    React.useEffect(() => {
        ref.current = value;
    });
    return ref.current as T;
}

const AttachmentManager: React.FunctionComponent<Props> = ({ resourceName, resourceId, acceptedExtensions, remoteFiles, onFilesAdded, onUploadStart, onUploadEnd }) => {
    const [state, setState] = React.useState<State>({ status: null, pendingFiles: null });
    const prevResourceId = usePrevious(resourceId);
    const prevResourceName = usePrevious(resourceName);

    React.useEffect(() => {
        if (resourceId != null) {
            const stored = AttachmentsStore.getByResource(resourceName, resourceId);
            if (!stored || stored.length === 0)
                AttachmentsActions.get(resourceName, resourceId);
        }
    }, [])

    React.useEffect(() => {
        if (resourceName != prevResourceName) {
            resetState();
            return;
        }

        if (resourceId != null) {
            if (prevResourceId == null) {
                uploadDocuments(state.pendingFiles);
            } else {
                //svuoto la coda
                resetState();
            }
        }
    }, [resourceId, resourceName])

    const resetState = () => {
        setState({ pendingFiles: null, status: null });
    }

    const getIcon = (fileName: string) => {
        let iconClass = "far fa-file";
        const pointIndex = fileName.lastIndexOf(".");
        const ex = pointIndex != -1 ? fileName.substr(pointIndex + 1) : null;
        switch (ex) {
            case "jpg":
            case "jpeg":
            case "gif":
            case "png":
            case "tif":
            case "tiff":
                iconClass = "far fa-image";
                break;
            case "txt":
                iconClass = "far fa-file-alt"
                break;
            case "doc":
            case "docx":
                iconClass = "far fa-file-alt"
                break;
            case "doc":
            case "docx":
                iconClass = "far fa-file-word"
                break;
            case "xls":
            case "xlsx":
                iconClass = "far fa-file-excel"
                break;
            case "pdf":
                iconClass = "far fa-file-pdf"
                break;

        }

        return <i className={`${iconClass} fa-5x`} title={fileName} />
    }

    const onAddDocuments = async (files: FileList | null) => {
        if (files == null) return;

        if (resourceId != null)
            await uploadDocuments(files);
        else {

            let pending: File[] = [];
            for (let i = 0; i < files.length; i++)
                pending.push(files[i])

            const pendingFiles = [...(state.pendingFiles ?? []), ...pending];
            setState({ status: null, pendingFiles: pendingFiles });
            onFilesAdded?.(ToAttachmentsInfo(pendingFiles));
        }
    };

    const uploadDocuments = async (files: FileList | File[] | null) => {
        if (files == null) return;

        onUploadStart?.(ToAttachmentsInfo(files));
        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            await AttachmentsActions.upload(resourceName, resourceId!, { fileName: resourceName, data: file });
            let cloned = { ...state, status: `(${i + 1}/${files.length}) Caricamento file ${file.name} in corso...` };
            setState(cloned);
        }
        onUploadEnd?.(ToAttachmentsInfo(files));
        resetState();
    };

    const onDownloadFile = async (fileKey: string) => {
        await AttachmentsActions.download(fileKey);
    }

    const onDeleteFile = async (fileKey: string) => {
        if (await confirmMessage("Sei sicuro di voler eliminare l'allegato?")) {
            await AttachmentsActions.delete(fileKey)
        }
    }

    return (
        <ul className="list-group" style={{ overflowX: "auto" }}>
            {remoteFiles && remoteFiles.length > 0 && remoteFiles.map(file => <li key={file.key}
                className="list-group-item d-flex align-items-center flex-wrap">
                <div className="d-flex align-items-center">
                    <div className="me-2">
                        {file.previewAvailable && <AttachmentPreview
                            className="rounded"
                            fileKey={file.key}
                            height={40}
                        />}
                        {!file.previewAvailable && <div style={{ height: 40 }} className="d-flex justify-content-center align-items-center">
                            {getIcon(file.fileName)}
                        </div>}
                    </div>
                </div>
                <div>
                    <span className="ms-2" style={{ fontSize: "0.9em" }}>
                        {file.fileName}
                    </span>
                </div>
                <div className="btn-group ms-auto me-0">
                    <a className="btn btn-info text-white"
                        onClick={onDownloadFile.bind(null, file.key)}>
                        <i className="fas fa-download" />
                    </a>
                    <a className="btn btn-danger text-white"
                        onClick={onDeleteFile.bind(null, file.key)}>
                        <i className="fas fa-trash" />
                    </a>
                </div>
            </li>)}
            <li className="list-group-item list-group-item-info">
                <FileInput
                    label="Allega un documento"
                    onChange={onAddDocuments}
                    accept={acceptedExtensions ?? [".jpg", ".jpeg", ".gif", ".png", ".tif", ".tiff", ".txt", ".doc", ".docx", ".xls", ".xlsx", ".pdf"]}
                    multiple
                />
                {state.status && (<div className="progress">
                    <div className="progress-bar progress-bar-striped progress-bar-animated" role="progressbar" style={{ width: "100%" }}>{state.status}</div>
                </div>)}
            </li>
        </ul>
    );
}

export default attach(AttachmentManager, AttachmentsStore, (store, props) => {
    return { remoteFiles: props.resourceId ? store.getByResource(props.resourceName, props.resourceId) : [] }
})